import { RTKTags, api } from '@/store/api'

import Env from '../../../env/env'

import type {
  createDigitalFootprintRequest,
  editDigitalFootprintRequest,
  updateUserDataRequest,
  UpdateUserMotivationRequest,
  UpdateUserPdfConfigRequest,
  UserContactUsRequest,
  UserInfoCrudResponse,
  UserInfoResponse,
  FaceAnalysisResponse,
  FaceAnalysisRequest,
} from './Users.types'

export const usersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // useUserInfoQuery
    // useLazyUserInfoQuery
    userInfo: builder.query<UserInfoResponse, void>({
      query: () => ({
        url: `${Env.BASE_API_URL}/api/v1/users/me`,
        method: 'GET',
      }),
      transformResponse: (response: UserInfoCrudResponse) => {
        return response.info
      },
      providesTags: [RTKTags.userInfo, RTKTags.persona],
    }),

    // useUserContactMutation
    userContact: builder.mutation<void, UserContactUsRequest>({
      query: (request) => ({
        url: `${Env.BASE_API_URL}/api/v1/forms/contact-us`,
        method: 'POST',
        body: {
          message: request.message,
        },
      }),
    }),

    // useUpdateUserDataMutation
    updateUserData: builder.mutation<void, updateUserDataRequest>({
      query: (request) => ({
        url: `${Env.BASE_API_URL}/api/v1/users`,
        method: 'PUT',
        body: {
          language: request.language,
        },
      }),
      invalidatesTags: [RTKTags.userInfo],
    }),

    // useUpdateUserPdfConfigMutation
    updateUserPdfConfig: builder.mutation<void, UpdateUserPdfConfigRequest>({
      query: (request) => ({
        url: `${Env.BASE_API_URL}/api/v1/users/pdf-config`,
        method: 'PATCH',
        body: {
          pdfLanguage: request.pdfLanguage,
        },
      }),
      invalidatesTags: [RTKTags.userInfo],
    }),

    // useUpdateUserMotivationMutation
    updateUserMotivation: builder.mutation<void, UpdateUserMotivationRequest>({
      query: (request) => ({
        url: `${Env.BASE_API_URL}/api/v1/users/motivation`,
        method: 'PATCH',
        body: {
          motivation: request.motivation,
        },
      }),
      invalidatesTags: [RTKTags.userInfo],
    }),

    // useUpdateProfileMutation
    updateProfile: builder.mutation<void, FormData>({
      query: (formData) => ({
        url: `${Env.BASE_API_URL}/api/v1/users/set-personal-info`,
        method: 'PATCH',
        body: formData,
      }),
      invalidatesTags: [RTKTags.userInfo],
    }),

    // useUpdateUserDigitalFootprintMutation
    createUserDigitalFootprint: builder.mutation<
      void,
      createDigitalFootprintRequest
    >({
      query: (request) => ({
        url: `${Env.BASE_API_URL}/api/v1/users/digital-footprint`,
        method: 'PATCH',
        body: {
          socialMedias: request.socialMedias,
        },
      }),
      invalidatesTags: [RTKTags.userInfo],
    }),

    // useEditDigitalFootprintMutation
    editDigitalFootprint: builder.mutation<void, editDigitalFootprintRequest>({
      query: (request) => ({
        url: `${Env.BASE_API_URL}/api/v1/users/edit-digital-footprint`,
        method: 'PATCH',
        body: {
          pdfLanguage: request.pdfLanguage,
          socialMedias: request.socialMedias,
        },
      }),
      invalidatesTags: [RTKTags.userInfo],
    }),

    // useAnalyzeFaceMutation
    analyzeFace: builder.mutation<
      FaceAnalysisResponse,
      FaceAnalysisRequest | void
    >({
      query: (body) => ({
        url: `${Env.BASE_API_URL}/api/v1/socials/analyze-face`,
        method: 'POST',
        body: body ?? {},
      }),
    }),

    // useDeleteAccountMutation
    deleteAccount: builder.mutation<{ message: string; status: number }, void>({
      query: () => ({
        url: `${Env.BASE_API_URL}/api/v1/users`,
        method: 'DELETE',
      }),
    }),
  }),
})

export const {
  useUserInfoQuery,
  useUserContactMutation,
  useUpdateUserDataMutation,
  useLazyUserInfoQuery,
  useUpdateUserPdfConfigMutation,
  useUpdateUserMotivationMutation,
  useUpdateProfileMutation,
  useCreateUserDigitalFootprintMutation,
  useEditDigitalFootprintMutation,
  useAnalyzeFaceMutation,
  useDeleteAccountMutation,
} = usersApi
